import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DatabaseBackup, Loader2 } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { getBackupStatus, runBackup, type BackupRunResult } from '../../lib/settingsApi';

const BACKUP_KEY = ['settings', 'backup'] as const;

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * "Back up the whole app now" — sits right under the Google Drive card, since
 * Drive is where the backup goes. One button, then the list of what is already
 * in the backup folder (the nightly cron's dumps show up here too), so the
 * admin can see at a glance when the last copy was taken without opening Drive.
 */
export function BackupCard() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: BACKUP_KEY, queryFn: getBackupStatus });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackupRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRun = Boolean(status.data?.driveConfigured) && !running && !status.data?.running;

  async function handleRun() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await runBackup());
      await qc.invalidateQueries({ queryKey: BACKUP_KEY });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
    } finally {
      setRunning(false);
    }
  }

  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
          <DatabaseBackup className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('settings.backup.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('settings.backup.description')}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleRun}
          disabled={!canRun}
          className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {running && <Loader2 className="h-4 w-4 animate-spin" />}
          {running ? t('settings.backup.running') : t('settings.backup.run')}
        </button>
        {running && (
          <span className="text-xs text-gray-500">{t('settings.backup.runningHint')}</span>
        )}
        {status.data && !status.data.driveConfigured && (
          <span className="text-xs text-amber-600">{t('settings.backup.driveOff')}</span>
        )}
      </div>

      {/* The unattended run: when it fires next and how the last one went. */}
      {status.data && (
        <div className="mt-3 space-y-0.5 text-xs text-gray-500">
          {status.data.schedule.enabled ? (
            <p>
              {t('settings.backup.schedule.daily', {
                time: status.data.schedule.time,
                zone: status.data.schedule.timeZone,
              })}
              {status.data.schedule.nextRunAt && (
                <>
                  {' · '}
                  {t('settings.backup.schedule.next', {
                    date: when.format(new Date(status.data.schedule.nextRunAt)),
                  })}
                </>
              )}
            </p>
          ) : (
            <p>{t('settings.backup.schedule.off')}</p>
          )}
          {status.data.schedule.lastRun && (
            <p className={status.data.schedule.lastRun.ok ? '' : 'text-red-500'}>
              {status.data.schedule.lastRun.ok
                ? t('settings.backup.schedule.lastOk', {
                    date: when.format(new Date(status.data.schedule.lastRun.at)),
                  })
                : t('settings.backup.schedule.lastFailed', {
                    date: when.format(new Date(status.data.schedule.lastRun.at)),
                    error: status.data.schedule.lastRun.error ?? '',
                  })}
            </p>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-400">
          <p className="font-medium">
            {t('settings.backup.success', { duration: Math.round(result.durationMs / 1000) })}
          </p>
          <ul className="mt-1 list-inside list-disc">
            <li>
              {t('settings.backup.database')}: {result.database.key.split('/').pop()} (
              {formatBytes(result.database.bytes)})
            </li>
            <li>
              {t('settings.backup.knowledgeBase')}: {result.knowledgeBase.key.split('/').pop()} (
              {formatBytes(result.knowledgeBase.bytes)},{' '}
              {t('settings.backup.files', { count: result.knowledgeBase.files })})
            </li>
          </ul>
        </div>
      )}
      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          {t('settings.backup.error')}: {error}
        </p>
      )}

      <div className="mt-5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('settings.backup.recent')}
        </h4>
        {status.isLoading && (
          <p className="mt-2 text-sm text-gray-500">{t('settings.integrations.loading')}</p>
        )}
        {status.data?.driveError && (
          <p className="mt-2 text-sm text-red-500">
            {t('settings.backup.loadError')}: {status.data.driveError}
          </p>
        )}
        {status.data && !status.data.driveError && status.data.backups.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">{t('settings.backup.none')}</p>
        )}
        {status.data && status.data.backups.length > 0 && (
          <ul className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200">
            {status.data.backups.map((b) => (
              <li
                key={b.key}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm"
              >
                <span className="min-w-0 truncate font-mono text-xs text-gray-700">{b.name}</span>
                <span className="shrink-0 text-xs text-gray-500">
                  {b.modifiedTime ? when.format(new Date(b.modifiedTime)) : '—'} ·{' '}
                  {formatBytes(b.size)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
